// Owner: library (plan §9.8, §3.1). The Library's state lives in the URL, so every view can be linked,
// reloaded and navigated back to: `/library?space=&type=&q=&parent=&sort=&owner=&access=&object=&view=`.
// No message keys here (the route loads with the app's route tree).
import { z } from 'zod';
import type { LibraryKind, LibraryListQuery, LibrarySpace } from '@dataroom/contracts/pages';
import { oneOf } from '../../lib/choice';
import { LIBRARY_KINDS, LIBRARY_SORTS, LIBRARY_SPACES } from '@dataroom/core/search-enums';

/** Spaces of the rail: the list spaces of the API plus the template gallery. */
export const RAIL_SPACES = [...LIBRARY_SPACES, 'templates'] as const;
export type RailSpace = (typeof RAIL_SPACES)[number];
export const LIBRARY_VIEWS = ['grid', 'list'] as const;
export type LibraryView = (typeof LIBRARY_VIEWS)[number];

/** `?type=dashboards` (the plural spelling of the plan) means the same as `?type=dashboard`. */
const singularKinds = (v: unknown) =>
  typeof v === 'string'
    ? v.replace(/(^|,)(folder|page|deck|document_template|dashboard)s(?=,|$)/g, '$1$2')
    : v;

const kindList = z.preprocess(
  singularKinds,
  z.string().regex(new RegExp(`^(${LIBRARY_KINDS.join('|')})(,(${LIBRARY_KINDS.join('|')}))*$`)),
);

export const LibrarySearch = z.object({
  space: z.enum(RAIL_SPACES).optional().catch(undefined),
  /** Kinds, comma separated (`page`, `page,deck`). */
  type: kindList.optional().catch(undefined),
  q: z.string().max(200).optional().catch(undefined),
  /** Show what is inside this folder (or the subpages of this page). */
  parent: z.uuid().optional().catch(undefined),
  sort: z.enum(LIBRARY_SORTS).optional().catch(undefined),
  owner: z.enum(['me', 'others']).optional().catch(undefined),
  access: z.enum(['private', 'shared', 'workspace', 'published']).optional().catch(undefined),
  object: z
    .string()
    .regex(/^[a-z][a-z0-9_]{0,62}$/)
    .optional()
    .catch(undefined),
  view: z.enum(LIBRARY_VIEWS).optional().catch(undefined),
});
export type LibrarySearch = z.infer<typeof LibrarySearch>;

export const kindsOf = (type: string | undefined): LibraryKind[] =>
  type ? type.split(',').flatMap((kind) => oneOf(kind, LIBRARY_KINDS) ?? []) : [];

/** The list space shown for a search state (`templates` has its own view). */
export function listSpaceOf(s: LibrarySearch): LibrarySpace {
  if (s.parent) return 'all';
  if (!s.space || s.space === 'templates') return 'all';
  return s.space;
}

/** The API query of the main list. */
export function listQueryOf(s: LibrarySearch): LibraryListQuery {
  const space = listSpaceOf(s);
  const q = s.q?.trim();
  const kinds = kindsOf(s.type);
  const tree = (space === 'mine' || space === 'workspace') && !q;
  const query: LibraryListQuery = { space, limit: 60 };
  if (q) query.q = q;
  if (s.parent && !q) query.parentId = s.parent;
  else if (tree) query.parentId = 'root';
  // A type filter narrows the documents; while browsing a level its folders stay, so you can go in.
  if (kinds.length) query.kind = query.parentId && !kinds.includes('folder') ? [...kinds, 'folder'] : kinds;
  if (s.sort) query.sort = s.sort;
  if (s.owner) query.owner = s.owner;
  if (s.access) query.access = s.access;
  if (s.object) query.objectKey = s.object;
  return query;
}

/**
 * Filters that narrow the list (the "Clear filters" button resets them). One chosen type is the
 * selected tab of the type bar, not a filter: an empty PDFs tab is empty, not "filtered".
 */
export const hasFilters = (s: LibrarySearch) =>
  Boolean(kindsOf(s.type).length > 1 || s.owner || s.access || s.object || s.q?.trim());
